import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePlayer } from "@/contexts/PlayerContext";
import AudioPlayer from "@/components/AudioPlayer";
import { Button } from "@/components/ui/button";
import { Radio, SkipForward, Volume2, VolumeX } from "lucide-react";

const nav = [
  { to: "/feed", label: "Feed" },
  { to: "/tournaments", label: "Tournaments" },
  { to: "/leaderboard", label: "Leaderboard" },
  { to: "/hall-of-fame", label: "Legends" },
  { to: "/upload", label: "Upload" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { ambientOn, ambientPlaying, ambientMuted, ambientTrack, toggleAmbient, nextAmbient, unmuteAmbient, ambientVolume, setAmbientVolume } = usePlayer();
  const navigate = useNavigate();

  return (
    <div className="app-shell grain bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-xl">
        <div className="px-6 md:px-12 py-4 flex items-center gap-6">
          <Link to="/" data-testid="logo-home" className="flex items-center gap-2 group">
            <span className="rec-dot" />
            <span className="font-display text-2xl">Beat<span className="text-primary">Battle</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 ml-6">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-mono-acc text-xs px-3 py-2 border border-transparent hover:border-border ${
                    isActive ? "text-secondary border-border" : "text-muted-foreground"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            {/* Ambient mode toggle */}
            <div className="hidden md:flex items-center border border-border">
              <button
                data-testid="ambient-toggle"
                onClick={ambientOn && ambientMuted ? unmuteAmbient : toggleAmbient}
                title={
                  ambientOn && ambientMuted
                    ? "Tap to unmute background beats"
                    : ambientOn
                      ? "Stop trap loop"
                      : "Play royalty-free trap beats"
                }
                className={`flex items-center gap-2 px-3 py-2 font-mono-acc text-[10px] border-r border-border ${
                  ambientOn && !ambientMuted
                    ? "bg-secondary text-background"
                    : ambientOn && ambientMuted
                      ? "bg-primary text-white animate-pulse"
                      : "hover:bg-muted"
                }`}
              >
                {ambientOn && ambientMuted ? (
                  <VolumeX size={12} />
                ) : (
                  <Radio size={12} className={ambientPlaying ? "animate-pulse" : ""} />
                )}
                {ambientOn && ambientMuted
                  ? "TAP TO UNMUTE"
                  : ambientOn
                    ? "TRAP LOOP ON"
                    : "TRAP LOOP"}
              </button>
              {ambientOn && (
                <>
                  <button
                    data-testid="ambient-next"
                    onClick={nextAmbient}
                    title="Skip to next ambient track"
                    className="px-2 py-2 border-r border-border hover:bg-muted"
                  >
                    <SkipForward size={12} />
                  </button>
                  <input
                    data-testid="ambient-volume"
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={ambientVolume}
                    onChange={(e) => setAmbientVolume(parseFloat(e.target.value))}
                    className="w-16 mx-2 accent-secondary"
                    title={`Volume: ${Math.round(ambientVolume * 100)}%`}
                  />
                </>
              )}
            </div>

            {user && typeof user === "object" ? (
              <>
                <Link
                  data-testid="profile-link"
                  to="/profile"
                  className="font-mono-acc text-xs text-muted-foreground hover:text-foreground border border-border px-3 py-2"
                >
                  @{user.username || "you"}
                </Link>
                <Button
                  data-testid="logout-btn"
                  onClick={async () => { await logout(); navigate("/"); }}
                  variant="outline"
                  className="rounded-none font-mono-acc text-xs border-border bg-transparent hover:bg-primary hover:text-white"
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="font-mono-acc text-xs px-3 py-2 border border-border hover:bg-muted"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  data-testid="nav-register"
                  className="font-mono-acc text-xs px-3 py-2 border border-primary bg-primary text-white hover:bg-white hover:text-primary"
                >
                  Join
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 pb-32">{children}</main>

      <AudioPlayer />

      <footer className="border-t border-border bg-background py-6 px-6 md:px-12 text-xs font-mono-acc text-muted-foreground flex items-center justify-between flex-wrap gap-2">
        <span>// Beat Battle 2026 — Producers Arena</span>
        <span className="text-[10px]">
          {ambientOn && <>Trap loop: <span className="text-secondary">{ambientTrack.title}</span> · {ambientTrack.artist} · </>}
          v0.1
        </span>
      </footer>
    </div>
  );
}
